import { useState, useMemo, useEffect } from "react";
// import "./TransactionsTable.scss";
import toast from "react-hot-toast";
import { useGetUsersQuery } from "../../../features/api/userApiSlice";
import { Link } from "react-router-dom";
import { useVideo } from "../../../hooks/useVideo";
import LoadingSpinner from "../../loader/Loader";
import { useAdminDeleteUserMutation } from "../../../features/api/userApiSlice";
import ConfirmationModal from "../../confirmation/ConfirmationModal";
import { generatePageNumbers } from "../../../util/generatePageNumber";

const UserList = () => {
  const { formatDate } = useVideo();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [adminDeleteUser] = useAdminDeleteUserMutation();

  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // categories per page

  const {
    data: users = [], // ✅ safe default so maps never blow up
    isFetching, // use this for spinner
    isError,
    isLoading,
    refetch,
  } = useGetUsersQuery(undefined, {
    refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
    refetchOnFocus: true, // refresh when tab regains focus
    refetchOnReconnect: true, // refresh when network comes back
  });

  useEffect(() => {
    if (!users?.length && !isFetching && !isError) {
      refetch(); // one more nudge in case the first request was aborted in dev
    }
  }, [users?.length, isFetching, isError, refetch]);

  const [userFilter, setUserFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  // Filtered transactions
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    return users.filter((tx) => {
      const matchesUser =
        !userFilter ||
        `${tx?.firstName} ${tx?.lastName}`
          .toLowerCase()
          .includes(userFilter.toLowerCase());
      const matchesEmail =
        !emailFilter ||
        tx?.email.toLowerCase().includes(emailFilter.toLowerCase());
      return matchesUser && matchesEmail;
    });
  }, [users, userFilter, emailFilter]);

  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  const currentUsers = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredUsers.slice(startIdx, startIdx + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (userId) => {
    setUserToDelete(userId);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      const response = await adminDeleteUser({
        userId: userToDelete,
      }).unwrap();
      toast.success(response?.message || "User deleted successfully!");
      setUserToDelete(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error(err?.data?.message || "Failed to delete user.");
    }
  };

  const handleCancelDelete = () => {
    setUserToDelete(null);
    setIsModalOpen(false);
  };

  if (isLoading) return <LoadingSpinner />;

  if (!users || users.length === 0) {
    return (
      <div className="no-content">
        <div>
          <p>No users found</p>
        </div>
      </div>
    );
  } else if (isError)
    return (
      <div className="no-content">
        <div>
          <p>An Error occurred</p>
        </div>
      </div>
    );

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Users</h1>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by first or last name"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        />
        <input
          type="email"
          placeholder="Filter by email"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
        />
      </div>

      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>User</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentUsers?.map((tx) => (
            <tr key={tx?._id}>
              <td>{tx?._id}</td>
              <td>
                {tx?.firstName} {tx?.lastName}
              </td>
              <td>{tx?.email}</td>
              {tx?.roles?.map((role, index) => (
                <td key={index}>{role}</td>
              ))}
              <td>{tx?.status === "active" ? "Active" : "Inctive"}</td>
              <td>{formatDate(tx?.createdAt)}</td>
              <td className="actions-container">
                <button
                  className="refund-btn"
                  onClick={() => handleDeleteClick(tx?.id || tx?._id)}
                >
                  Delete
                </button>
                <Link
                  className="ed-button"
                  aria-label={`edit ${tx?.firstName}`}
                  to={`/admin/users/edit-user/${tx?._id}`}
                >
                  Edit
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
        <ConfirmationModal
          isOpen={isModalOpen}
          message="Are you sure you want to delete this user? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserList;
